import { useState, useCallback, useEffect } from "react";
import AuditService from "../../../../services/audit_service";
import { useTenant } from "../../../../hooks/useTenant";

const useAuditLogs = () => {
    const { idTenant } = useTenant();

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    const [filters, setFilters] = useState({
        module: "",
        action: "",
        startDate: "",
        endDate: "",
        userId: ""
    });

    const fetchLogs = useCallback(async (isLoadMore = false, currentFilters = filters) => {
        if (!idTenant || (isLoadMore && !hasMore)) return;

        setLoading(true);
        setError(null);

        try {
            // Se for nova busca (isLoadMore = false), passa null em lastVisible.
            // Se for paginação, passa o lastVisible.
            const cursor = isLoadMore ? lastVisible : null;
            const response = await AuditService.getLogs(idTenant, {
                limit: 50,
                lastVisible: cursor,
                filters: {
                    module: currentFilters.module || undefined,
                    action: currentFilters.action || undefined,
                    startDate: currentFilters.startDate || undefined,
                    endDate: currentFilters.endDate || undefined,
                    userId: currentFilters.userId || undefined
                }
            });

            if (isLoadMore) {
                setLogs(prev => [...prev, ...response.data]);
            } else {
                setLogs(response.data);
            }

            setLastVisible(response.lastVisible);
            setHasMore(response.hasMore);
        } catch (err) {
            console.error("Erro ao carregar logs:", err);
            setError("Não foi possível carregar o histórico de auditoria.");
        } finally {
            setLoading(false);
        }
    }, [idTenant, lastVisible, hasMore, filters]);


    // Disparado sempre que filters mudam
    useEffect(() => {
        // Zera o cursor localmente para forçar a busca do início,
        // mas o fetchLogs já entende isso quando passamos `false`
        fetchLogs(false, filters);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, idTenant]); // Só roda quando filtros ou tenant mudarem


    const loadMore = useCallback(() => {
        fetchLogs(true);
    }, [fetchLogs]);

    const updateFilter = useCallback((key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
    }, []);

    const clearFilters = useCallback(() => {
        setFilters({
            module: "",
            action: "",
            startDate: "",
            endDate: "",
            userId: ""
        });
    }, []);

    return {
        logs,
        loading,
        error,
        hasMore,
        filters,
        loadMore,
        updateFilter,
        clearFilters,
        refresh: () => fetchLogs(false)
    };
};

export default useAuditLogs;
